import { supabase } from '@/lib/supabase';

export type ActivityCountByUser = {
  userId: string;
  count: number;
};

export type CarActivityCounts = {
  issuesNew: number;
  issuesByUser: ActivityCountByUser[];
  tasksUpdated: number;
  tasksByUser: ActivityCountByUser[];
  serviceRecordsUpdated: number;
  servicesByUser: ActivityCountByUser[];
  expensesAdded: number;
  expensesByUser: ActivityCountByUser[];
  carDetailsUpdated: number;
  carDetailsByUser: ActivityCountByUser[];
};

export type CarActivitySummary = {
  carId: string;
  counts: CarActivityCounts;
};

export type ActivityListItem = {
  id: string;
  title: string;
  timestamp: string;
  href: string;
  activityType: 'issue' | 'task' | 'service' | 'expense' | 'car_detail';
  userId: string;
};

export type CarActivityDetails = CarActivitySummary & {
  issues: ActivityListItem[];
  tasks: ActivityListItem[];
  services: ActivityListItem[];
  expenses: ActivityListItem[];
  carDetails: ActivityListItem[];
};

// Activity type mapping
const ACTIVITY_TYPE_MAP = {
  issue: 'symptoms',
  task: 'tasks',
  service: 'service_entries',
  expense: 'expenses',
  car_detail: 'car_change_notifications',
} as const;

type ActivityType = keyof typeof ACTIVITY_TYPE_MAP;

/**
 * Truncate UUID for logging (first 8 chars)
 */
function truncateId(id: string): string {
  return id ? `${id.substring(0, 8)}...` : 'unknown';
}

/**
 * Group items by user ID
 */
function groupByUser(items: any[], userIdField: string = 'user_id'): ActivityCountByUser[] {
  const userCounts = new Map<string, number>();
  items.forEach((item: any) => {
    const uid = item[userIdField];
    if (uid) {
      userCounts.set(uid, (userCounts.get(uid) || 0) + 1);
    }
  });
  return Array.from(userCounts.entries()).map(([userId, count]) => ({ userId, count }));
}

/**
 * Fetch dismissed activity IDs for a user
 */
async function getDismissedIds(userId: string, activityType: ActivityType): Promise<string[]> {
  const { data, error } = await supabase
    .from('user_activity_dismissals')
    .select('activity_id')
    .eq('user_id', userId)
    .eq('activity_type', activityType);

  if (error) {
    console.error(`❌ [User ${truncateId(userId)}] Error fetching dismissed ${activityType}:`, error);
    return [];
  }

  const dismissedIds = (data || []).map((row) => row.activity_id as string);
  if (dismissedIds.length > 0) {
    console.log(
      `🔕 [User ${truncateId(userId)}] Found ${dismissedIds.length} dismissed ${activityType} items:`,
      dismissedIds.map(truncateId)
    );
  }

  return dismissedIds;
}

/**
 * Dismiss a single activity item
 */
export async function dismissActivityItem(
  userId: string,
  activityType: ActivityType,
  activityId: string
): Promise<void> {
  console.log(
    `🗑️ [User ${truncateId(userId)}] Dismissing ${activityType} item ${truncateId(activityId)}...`
  );

  const { error } = await supabase
    .from('user_activity_dismissals')
    .upsert(
      {
        user_id: userId,
        activity_type: activityType,
        activity_id: activityId,
      },
      { onConflict: 'user_id,activity_type,activity_id' }
    );

  if (error) {
    console.error(
      `❌ [User ${truncateId(userId)}] Error dismissing ${activityType} ${truncateId(activityId)}:`,
      error
    );
    throw error;
  }

  console.log(
    `✅ [User ${truncateId(userId)}] Successfully dismissed ${activityType} item ${truncateId(activityId)}`
  );
}

/**
 * Dismiss all activity items in bulk
 */
export async function dismissAllActivityItems(
  userId: string,
  items: Array<{ type: ActivityType; id: string }>
): Promise<void> {
  if (items.length === 0) {
    console.log(`⚠️ [User ${truncateId(userId)}] No items to dismiss`);
    return;
  }

  console.log(
    `🗑️ [User ${truncateId(userId)}] Dismissing all ${items.length} items:`,
    items.map((item) => `${item.type}:${truncateId(item.id)}`)
  );

  const dismissals = items.map((item) => ({
    user_id: userId,
    activity_type: item.type,
    activity_id: item.id,
  }));

  const { error } = await supabase.from('user_activity_dismissals').upsert(dismissals, {
    onConflict: 'user_id,activity_type,activity_id',
  });

  if (error) {
    console.error(`❌ [User ${truncateId(userId)}] Error dismissing all items:`, error);
    throw error;
  }

  console.log(`✅ [User ${truncateId(userId)}] Successfully dismissed all ${items.length} items`);
}

/**
 * Fetch activity summary (counts only) for a car
 */
export async function fetchCarActivitySummary(carId: string, userId: string): Promise<CarActivitySummary> {
  console.log(
    `🔍 [User ${truncateId(userId)}] Fetching activity summary for car ${truncateId(carId)}...`
  );

  // Fetch dismissed IDs for each activity type
  const [dismissedIssues, dismissedTasks, dismissedServices, dismissedExpenses, dismissedCarDetails] =
    await Promise.all([
      getDismissedIds(userId, 'issue'),
      getDismissedIds(userId, 'task'),
      getDismissedIds(userId, 'service'),
      getDismissedIds(userId, 'expense'),
      getDismissedIds(userId, 'car_detail'),
    ]);

  console.log(
    `📋 [User ${truncateId(userId)}] Dismissed counts: issues=${dismissedIssues.length}, tasks=${dismissedTasks.length}, services=${dismissedServices.length}, expenses=${dismissedExpenses.length}, carDetails=${dismissedCarDetails.length}`
  );

  // Fetch all activity IDs and user IDs from other users (we'll filter dismissed items client-side)
  const [issuesRes, tasksRes, servicesRes, expensesRes, carDetailsRes] = await Promise.all([
    supabase
      .from('symptoms')
      .select('id, user_id')
      .eq('car_id', carId)
      .is('deleted_at', null)
      .neq('user_id', userId),
    supabase
      .from('tasks')
      .select('id, user_id')
      .eq('car_id', carId)
      .neq('user_id', userId),
    supabase
      .from('service_entries')
      .select('id, user_id')
      .eq('car_id', carId)
      .neq('user_id', userId),
    supabase
      .from('expenses')
      .select('id, user_id')
      .eq('car_id', carId)
      .is('deleted_at', null)
      .neq('user_id', userId),
    supabase
      .from('car_change_notifications')
      .select('id, changed_by')
      .eq('car_id', carId)
      .neq('changed_by', userId),
  ]);

  // Surface the first error encountered
  const firstError =
    issuesRes.error ||
    tasksRes.error ||
    servicesRes.error ||
    expensesRes.error ||
    carDetailsRes.error;
  if (firstError) {
    console.error('❌ Error fetching activity summary:', firstError);
    throw firstError;
  }

  // Filter out dismissed items and count
  const allIssues = issuesRes.data || [];
  const allTasks = tasksRes.data || [];
  const allServices = servicesRes.data || [];
  const allExpenses = expensesRes.data || [];
  const allCarDetails = carDetailsRes.data || [];

  const visibleIssues = allIssues.filter((row: any) => !dismissedIssues.includes(row.id));
  const visibleTasks = allTasks.filter((row: any) => !dismissedTasks.includes(row.id));
  const visibleServices = allServices.filter((row: any) => !dismissedServices.includes(row.id));
  const visibleExpenses = allExpenses.filter((row: any) => !dismissedExpenses.includes(row.id));
  const visibleCarDetails = allCarDetails.filter((row: any) => !dismissedCarDetails.includes(row.id));

  const counts = {
    issuesNew: visibleIssues.length,
    issuesByUser: groupByUser(visibleIssues, 'user_id'),
    tasksUpdated: visibleTasks.length,
    tasksByUser: groupByUser(visibleTasks, 'user_id'),
    serviceRecordsUpdated: visibleServices.length,
    servicesByUser: groupByUser(visibleServices, 'user_id'),
    expensesAdded: visibleExpenses.length,
    expensesByUser: groupByUser(visibleExpenses, 'user_id'),
    carDetailsUpdated: visibleCarDetails.length,
    carDetailsByUser: groupByUser(visibleCarDetails, 'changed_by'),
  };

  console.log(
    `📊 [User ${truncateId(userId)}] Activity summary for car ${truncateId(carId)}:`,
    {
      issuesNew: counts.issuesNew,
      tasksUpdated: counts.tasksUpdated,
      serviceRecordsUpdated: counts.serviceRecordsUpdated,
      expensesAdded: counts.expensesAdded,
      carDetailsUpdated: counts.carDetailsUpdated,
    }
  );
  console.log(
    `   Total found: issues=${allIssues.length} (${visibleIssues.length} visible), tasks=${allTasks.length} (${visibleTasks.length} visible), services=${allServices.length} (${visibleServices.length} visible), expenses=${allExpenses.length} (${visibleExpenses.length} visible), carDetails=${allCarDetails.length} (${visibleCarDetails.length} visible)`
  );
  console.log(
    `👥 [User ${truncateId(userId)}] Users by activity:`,
    {
      issues: counts.issuesByUser.map((u) => `${truncateId(u.userId)}:${u.count}`),
      tasks: counts.tasksByUser.map((u) => `${truncateId(u.userId)}:${u.count}`),
      services: counts.servicesByUser.map((u) => `${truncateId(u.userId)}:${u.count}`),
      expenses: counts.expensesByUser.map((u) => `${truncateId(u.userId)}:${u.count}`),
      carDetails: counts.carDetailsByUser.map((u) => `${truncateId(u.userId)}:${u.count}`),
    }
  );

  return {
    carId,
    counts,
  };
}

/**
 * Fetch detailed activity list for a car
 */
export async function fetchCarActivityDetails(carId: string, userId: string): Promise<CarActivityDetails> {
  console.log(
    `🔍 [User ${truncateId(userId)}] Fetching activity details for car ${truncateId(carId)}...`
  );

  // Fetch dismissed IDs and activity data in parallel (don't duplicate fetchCarActivitySummary)
  const [dismissedIssues, dismissedTasks, dismissedServices, dismissedExpenses, dismissedCarDetails] =
    await Promise.all([
      getDismissedIds(userId, 'issue'),
      getDismissedIds(userId, 'task'),
      getDismissedIds(userId, 'service'),
      getDismissedIds(userId, 'expense'),
      getDismissedIds(userId, 'car_detail'),
    ]);

  const limit = 50;

  // Fetch all activity from other users (we'll filter dismissed items client-side)
  const [issues, tasks, services, expenses, carDetails] = await Promise.all([
    supabase
      .from('symptoms')
      .select('id, title, created_at, description, user_id')
      .eq('car_id', carId)
      .is('deleted_at', null)
      .neq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit),
    supabase
      .from('tasks')
      .select('id, title, updated_at, user_id')
      .eq('car_id', carId)
      .neq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(limit),
    supabase
      .from('service_entries')
      .select('id, shop_name, service_date, updated_at, user_id')
      .eq('car_id', carId)
      .neq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(limit),
    supabase
      .from('expenses')
      .select('id, category, amount_cents, expense_date, created_at, user_id')
      .eq('car_id', carId)
      .is('deleted_at', null)
      .neq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit),
    supabase
      .from('car_change_notifications')
      .select('id, created_at, status, changed_by')
      .eq('car_id', carId)
      .neq('changed_by', userId)
      .order('created_at', { ascending: false })
      .limit(limit),
  ]);

  const firstError =
    issues.error || tasks.error || services.error || expenses.error || carDetails.error;
  if (firstError) throw firstError;

  const allIssuesData = issues.data || [];
  const visibleIssuesData = allIssuesData.filter((row: any) => !dismissedIssues.includes(row.id));

  const issuesItems: ActivityListItem[] = visibleIssuesData.map((row: any) => ({
    id: row.id,
    title: row.title || (typeof row.description === 'string' ? row.description.slice(0, 60) : 'Issue'),
    timestamp: row.created_at,
    href: `/symptom-summary/${carId}/${row.id}`,
    activityType: 'issue' as const,
    userId: row.user_id,
  }));

  console.log(
    `📝 [User ${truncateId(userId)}] Issues: ${allIssuesData.length} total, ${visibleIssuesData.length} visible (${dismissedIssues.length} dismissed)`
  );

  const allTasksData = tasks.data || [];
  const visibleTasksData = allTasksData.filter((row: any) => !dismissedTasks.includes(row.id));

  const taskItems: ActivityListItem[] = visibleTasksData.map((row: any) => ({
    id: row.id,
    title: row.title || 'Task',
    timestamp: row.updated_at,
    href: `/records/${carId}?tab=tasks`,
    activityType: 'task' as const,
    userId: row.user_id,
  }));

  console.log(
    `📝 [User ${truncateId(userId)}] Tasks: ${allTasksData.length} total, ${visibleTasksData.length} visible (${dismissedTasks.length} dismissed)`
  );

  const allServicesData = services.data || [];
  const visibleServicesData = allServicesData.filter((row: any) => !dismissedServices.includes(row.id));

  const serviceItems: ActivityListItem[] = visibleServicesData.map((row: any) => ({
    id: row.id,
    title: row.shop_name ? `Service: ${row.shop_name}` : 'Service record updated',
    timestamp: row.updated_at || row.service_date,
    href: `/services/view/${row.id}`,
    activityType: 'service' as const,
    userId: row.user_id,
  }));

  console.log(
    `📝 [User ${truncateId(userId)}] Services: ${allServicesData.length} total, ${visibleServicesData.length} visible (${dismissedServices.length} dismissed)`
  );

  const allExpensesData = expenses.data || [];
  const visibleExpensesData = allExpensesData.filter((row: any) => !dismissedExpenses.includes(row.id));

  const expenseItems: ActivityListItem[] = visibleExpensesData.map((row: any) => ({
    id: row.id,
    title:
      typeof row.amount_cents === 'number'
        ? `Expense added`
        : 'Expense added',
    timestamp: row.created_at || row.expense_date,
    href: `/expenses/view/${row.id}`,
    activityType: 'expense' as const,
    userId: row.user_id,
  }));

  console.log(
    `📝 [User ${truncateId(userId)}] Expenses: ${allExpensesData.length} total, ${visibleExpensesData.length} visible (${dismissedExpenses.length} dismissed)`
  );

  const allCarDetailsData = carDetails.data || [];
  const visibleCarDetailsData = allCarDetailsData.filter((row: any) => !dismissedCarDetails.includes(row.id));

  const carDetailsItems: ActivityListItem[] = visibleCarDetailsData.map((row: any) => ({
    id: row.id,
    title: 'Car details updated',
    timestamp: row.created_at,
    href: `/cars/${carId}`,
    activityType: 'car_detail' as const,
    userId: row.changed_by,
  }));

  console.log(
    `📝 [User ${truncateId(userId)}] Car details: ${allCarDetailsData.length} total, ${visibleCarDetailsData.length} visible (${dismissedCarDetails.length} dismissed)`
  );

  // Calculate counts from the visible items
  const counts: CarActivityCounts = {
    issuesNew: issuesItems.length,
    issuesByUser: groupByUser(visibleIssuesData, 'user_id'),
    tasksUpdated: taskItems.length,
    tasksByUser: groupByUser(visibleTasksData, 'user_id'),
    serviceRecordsUpdated: serviceItems.length,
    servicesByUser: groupByUser(visibleServicesData, 'user_id'),
    expensesAdded: expenseItems.length,
    expensesByUser: groupByUser(visibleExpensesData, 'user_id'),
    carDetailsUpdated: carDetailsItems.length,
    carDetailsByUser: groupByUser(visibleCarDetailsData, 'changed_by'),
  };

  const result: CarActivityDetails = {
    carId,
    counts,
    issues: issuesItems,
    tasks: taskItems,
    services: serviceItems,
    expenses: expenseItems,
    carDetails: carDetailsItems,
  };

  console.log(
    `✅ [User ${truncateId(userId)}] Activity details loaded: ${issuesItems.length} issues, ${taskItems.length} tasks, ${serviceItems.length} services, ${expenseItems.length} expenses, ${carDetailsItems.length} car details`
  );

  return result;
}
